import { AnimatePresence, motion, type Transition } from "framer-motion"
import { ChevronDown, MessageCircle } from "lucide-react"
import React, { useEffect, useRef, useState } from "react"
import type { BrackyFeature, Scenario } from "./data/bracky-features-data"
import { useIsDesktop } from "./hooks/useIsDesktop"
import { LandingAnimatedPaginator } from "./LandingAnimatedPaginator"
import { LandingChipComingSoon } from "./LandingChipComingSoon"
import { LandingChipFeature } from "./LandingChipFeature"
import { LandingSectionBrackyMessage } from "./LandingSectionBrackyMessage"
import { getScenarioIcon, renderScenarioDescription } from "./utils/bracky-feature-utils"

type FeatureSectionProps = {
  feature: BrackyFeature
  index: number
  activeIdx: number
  onSetActive: (idx: number) => void
}

export const BrackyFeatureSection = React.memo(function FeatureSection({
  feature,
  index,
  activeIdx,
  onSetActive,
}: FeatureSectionProps) {
  const isDesktop = useIsDesktop()
  const isActive = index === activeIdx
  const isEven = index % 2 === 0
  const sectionRef = useRef<HTMLElement>(null)

  const handleToggle = () => {
    if (isDesktop) return
    const willOpen = !isActive
    onSetActive(isActive ? -1 : index)

    if (willOpen && sectionRef.current) {
      setTimeout(() => {
        if (sectionRef.current) {
          const rect = sectionRef.current.getBoundingClientRect()
          const currentScrollY = window.scrollY
          const targetScrollY = currentScrollY + rect.top - 88

          window.scrollTo({
            top: targetScrollY,
            behavior: "smooth",
          })
        }
      }, 100)
    }
  }

  const marketEmojiOptions = [
    "🎯",
    "🎤",
    "🎮",
    "🎲",
    "🍿",
    "🏓",
    "🃏",
    "🎱",
    "🍺",
    "🎳",
    "🌎",
    "🤑",
    "🪀",
    "🏁",
    "📺",
    "🎬",
    "🔮",
    "🎟️",
    "👾",
  ]
  const [emojiIdx, setEmojiIdx] = useState(0)
  const [_emojiRotation, setEmojiRotation] = useState(2)
  const rotationSignRef = useRef(1)
  const recentIdxRef = useRef<number[]>([0])

  useEffect(() => {
    if (feature.title.startsWith("Create Your Own Markets")) {
      const id = setInterval(() => {
        setEmojiIdx((_prev) => {
          let next = Math.floor(Math.random() * marketEmojiOptions.length)
          const recent = recentIdxRef.current

          const MAX_HISTORY = 3
          while (marketEmojiOptions.length > 1 && recent.includes(next)) {
            next = Math.floor(Math.random() * marketEmojiOptions.length)
          }

          recent.push(next)
          if (recent.length > MAX_HISTORY) recent.shift()

          rotationSignRef.current *= -1
          setEmojiRotation(2 * rotationSignRef.current)

          return next
        })
      }, 1000)
      return () => clearInterval(id)
    }
    return undefined
  }, [feature.title, marketEmojiOptions.length])

  const isPutAMarket = feature.title.startsWith("Create Your Own Markets")

  let dynamicTitle: string
  if (feature.title === "AI Sports Intelligence™") {
    dynamicTitle = isDesktop ? "AI Sports Intelligence™" : "Get AI Edge"
  } else if (isPutAMarket) {
    dynamicTitle = `Create Your Own Markets ${marketEmojiOptions[emojiIdx]}`
  } else {
    dynamicTitle = feature.title
  }

  const buildTitleWords = () => {
    const emojiCurrent = marketEmojiOptions[emojiIdx]
    const words = isPutAMarket ? ["Create", "Your", "Own", "Markets"] : dynamicTitle.split(" ")

    let skipNext = false
    return words.flatMap((word, idx) => {
      if (skipNext) {
        skipNext = false
        return []
      }

      const isGradientWord = word === "AI"
      const needsLiveTM = word === "Live" && feature.title.includes("Bet Live")
      const classes = `inline-block mr-2 ${isGradientWord ? "shiny-text-ai-sports-agent" : ""}`

      if (isPutAMarket && word === "Your") {
        return [
          <span key={`word-${word}`} className={`${classes} -ml-[0.3rem]`}>
            {word}
          </span>,
          <br key={`br-${word}`} />,
        ]
      }

      if (feature.title === "AI Sports Intelligence™" && !isDesktop && word === "Your") {
        return [
          <span key="word-ai-your" className={`${classes} -ml-[0.3rem]`}>
            {word}
          </span>,
          <br key="br-ai-your" />,
        ]
      }

      if (feature.title === "AI Sports Intelligence™" && !isDesktop && word === "AI") {
        return (
          <span key="word-ai-ai" className={`${classes} -ml-[0.23rem]`}>
            {word}
          </span>
        )
      }

      if (needsLiveTM) {
        const liveElement = (
          <span key="word-live-tm" className={classes}>
            Live
            <span className="relative font-normal text-[0.45em] lg:text-[0.5em] text-white/85 top-[-0.62em] lg:top-[-0.48em]">
              ™
            </span>
          </span>
        )
        return [liveElement, <br key="br-live-tm" />]
      }

      if (isPutAMarket && word === "Own" && idx === 2) {
        return [
          <span key="word-own" className={classes}>
            {word}
          </span>,
          <span
            key="emoji-wrapper"
            className="inline-block relative w-[.9em] h-[.9em] mr-[.30em] -translate-y-[.4rem] sm:ml-[.033em] sm:mr-[.25em] align-middle lg:-translate-y-[0.55rem]"
          >
            <AnimatePresence initial={false}>
              <motion.span
                key={emojiCurrent}
                className="absolute inset-0 text-white"
                initial={{ scale: 0.8, opacity: 0, rotate: 0 }}
                animate={{
                  scale: 1,
                  opacity: 1,
                  rotate: 0,
                  filter: "brightness(1.08) drop-shadow(0 0 15px rgba(255,248,240,0.32))",
                }}
                exit={{ scale: 0.8, opacity: 0, rotate: 0 }}
                transition={{ duration: 0.18, ease: "easeInOut" }}
              >
                {emojiCurrent}
              </motion.span>
            </AnimatePresence>
          </span>,
        ]
      }

      if (word.includes("™")) {
        const wordWithoutTM = word.replace("™", "")
        return (
          <span key={`word-${wordWithoutTM}-tm`} className={classes}>
            {wordWithoutTM}
            <span className="relative font-normal text-[0.45em] lg:text-[0.5em] text-white/85 top-[-0.62em] lg:top-[-0.48em]">
              ™
            </span>
          </span>
        )
      }

      if (word === "Your" && feature.title.includes("In Your Feed")) {
        return (
          <span key="word-feed-your" className={`${classes} -ml-[0.3rem]`}>
            {word}
          </span>
        )
      }

      return (
        <span key={`word-${word}`} className={classes}>
          {word}
        </span>
      )
    })
  }

  const mdPhoneFirst = !isEven

  const contentOrderClass = `order-1 ${mdPhoneFirst ? "lg:order-2" : "lg:order-1"}`
  const phoneOrderClass = `order-2 ${mdPhoneFirst ? "lg:order-1" : "lg:order-2"}`

  const contentAlignmentClass = "lg:justify-self-start"
  const phoneAlignmentClass = "lg:justify-self-start"

  const scenarios = feature.scenarios ?? []
  const hasScenarios = scenarios.length > 0

  const [selectedScenarioIdx, setSelectedScenarioIdx] = useState(0)

  const activeChipRef = useRef<HTMLButtonElement | null>(null)
  const chipsWrapperRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (isDesktop) return

    if (!(chipsWrapperRef.current && activeChipRef.current)) return

    const wrapper = chipsWrapperRef.current
    const chip = activeChipRef.current

    const chipRight = chip.offsetLeft + chip.offsetWidth
    const targetScrollLeft = chipRight - wrapper.clientWidth + 16

    wrapper.scrollTo({ left: targetScrollLeft, behavior: "smooth" })
  }, [selectedScenarioIdx, isDesktop])

  const activeScenario: Scenario | null = (hasScenarios ? scenarios[selectedScenarioIdx] : null) ?? null

  const sectionVariants = {
    initial: {},
    animate: {
      transition: {
        staggerChildren: isDesktop ? 0.02 : 0.125,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.3, ease: "easeOut" } as Transition,
    },
  }

  const chipsMarkup = hasScenarios ? (
    <div className="flex gap-1.5 justify-start sm:justify-center mdlg:justify-center lg:justify-start pb-1 pl-[.77rem] sm:pl-0 lg:pl-0 sm:translate-x-[0.58rem] lg:translate-x-0">
      {scenarios.map((s, i) => {
        const isActive = selectedScenarioIdx === i
        const IconComponent = getScenarioIcon(s.label)
        return (
          <LandingChipFeature
            key={s.label}
            label={s.label}
            icon={IconComponent}
            isActive={isActive}
            onClick={() => setSelectedScenarioIdx(i)}
            ref={i === selectedScenarioIdx ? activeChipRef : null}
          />
        )
      })}
      <div className="sm:hidden opacity-0 pointer-events-none">
        <LandingChipFeature label="·" icon={MessageCircle} isActive={false} onClick={() => undefined} />
      </div>
    </div>
  ) : null

  const motionSettings = isDesktop
    ? { variants: itemVariants, initial: "hidden", whileInView: "visible", viewport: { once: true, amount: 0.3 } }
    : {}

  const getDesktopAlignment = () => {
    switch (index) {
      case 0:
        return "lg:-mt-5 xl:-mt-8 2xl:-mt-10"
      case 1:
        return "lg:mt-5"
      case 2:
        return "lg:mt-6"
      case 3:
        return "lg:mt-.5"
      default:
        return ""
    }
  }

  const content = (
    <motion.div
      className={`flex flex-col w-full lg:max-w-120 mx-0 ${contentOrderClass} ${contentAlignmentClass} ${getDesktopAlignment()}`}
      {...motionSettings}
    >
      {isDesktop && (
        <div
          className={`relative flex justify-center ${index === 3 ? "mb-[1.05rem] lg:mb-[0.2rem]" : "mb-[1.3rem] lg:mb-2"} lg:mt-6`}
        >
          {index === 3 ? <LandingChipComingSoon /> : <LandingAnimatedPaginator total={3} active={index} />}
        </div>
      )}

      <div className="flex flex-col justify-center space-y-[0.7rem] lg:space-y-4 lg:mt-3.5">
        <div className="flex flex-col relative">
          {isDesktop && (
            <motion.h3
              className="font-extrabold leading-none pb-[0.3em] -mb-[1.2rem] mx-auto text-center lg:mx-0 lg:text-left text-h2 bg-linear-to-b from-white to-[#dce3fe] bg-clip-text text-transparent"
              style={{ letterSpacing: "-0.1rem" }}
              variants={{
                initial: {},
                animate: { transition: { staggerChildren: 0.04 } },
              }}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, amount: 0.6 }}
            >
              {buildTitleWords()}
            </motion.h3>
          )}
          {feature.title === "Live Market Creation" && (
            <div className="mt-2 ml-4 lg:ml-0">
              <span className="inline-block px-3 py-1 rounded-full bg-linear-to-r from-[#001CFF]/20 to-[#00EAFF]/20 border border-[#001CFF]/30 z-100 text-[#00EAFF] text-xs font-semibold tracking-wide backdrop-blur-xl">
                COMING SOON
              </span>
            </div>
          )}
        </div>

        {hasScenarios && <div className="hidden lg:block mt-3">{chipsMarkup}</div>}

        <p className="text-white/70 text-[1.0625rem] leading-[1.43] hidden lg:block min-h-18">
          {renderScenarioDescription(activeScenario, feature)}
        </p>
      </div>
    </motion.div>
  )

  const phone = (
    <motion.div
      className={`relative w-full lg:max-w-lg flex-none flex flex-col items-center lg:items-start mx-0 ${phoneOrderClass} ${phoneAlignmentClass} ${!isDesktop ? "mb-3" : ""}`}
      {...motionSettings}
    >
      {hasScenarios && (
        <motion.div
          variants={{
            initial: { opacity: 0, y: 10 },
            animate: { opacity: 1, y: 0, transition: { duration: 0.2, ease: "easeOut" } },
          }}
        >
          <div
            ref={chipsWrapperRef}
            className="lg:hidden mb-4 mt-[-1.3rem] w-screen max-w-[100vw] overflow-x-auto scrollbar-hide relative left-1/2 -translate-x-1/2 -mr-4"
          >
            <div className="pl-2 pr-6">{chipsMarkup}</div>
          </div>
        </motion.div>
      )}
      <motion.div
        variants={{
          initial: { opacity: 0, y: 10 },
          animate: { opacity: 1, y: 0, transition: { duration: 0.2, ease: "easeOut" } },
        }}
      >
        <AnimatePresence mode="wait" initial={false}>
          <LandingSectionBrackyMessage
            key={selectedScenarioIdx}
            userMessage={activeScenario?.userMessage ?? ""}
            brackyMessage={activeScenario?.brackyMessage ?? ""}
            username={activeScenario?.username ?? feature.username ?? "defaultuser"}
            userTimestamp={activeScenario?.userTimestamp ?? ""}
            brackyTimestamp={activeScenario?.brackyTimestamp ?? ""}
            description={renderScenarioDescription(activeScenario, feature, !isDesktop)}
            showDescriptionOnMobile={true}
          />
        </AnimatePresence>
      </motion.div>
    </motion.div>
  )

  const isLast = index === 3
  const sectionPadding = index === 0 ? "pt-12 pb-12 sm:pb-16 lg:pb-14" : "py-10 sm:py-14 lg:py-12"

  const desktopBottomGap = isLast ? "lg:mb-8" : ""

  const headerMarkup = (
    <div className="flex flex-col flex-1 overflow-visible w-[85%] mx-auto">
      <div className={`relative flex justify-center mt-[0.7rem] ${index === 3 ? "mb-[-0.7rem]" : "mb-[-.5rem]"}`}>
        {index === 3 ? <LandingChipComingSoon /> : <LandingAnimatedPaginator total={3} active={index} />}
      </div>
      <motion.h3
        className={`font-extrabold leading-none pb-[0.3em] pt-[0.5em] -mb-[1.2rem] mx-auto text-center text-h2 bg-linear-to-b from-white to-[#dce3fe] bg-clip-text text-transparent overflow-visible transition-all duration-400 group-active:opacity-50 ${isActive ? "opacity-100" : "opacity-100"}`}
        style={{ letterSpacing: "-0.1rem" }}
        variants={{}}
      >
        {buildTitleWords()}
      </motion.h3>
    </div>
  )

  if (!isDesktop) {
    return (
      <section ref={sectionRef} className="mb-[1.2rem] px-2 relative">
        {index !== 2 && index !== 3 && (
          <div className="absolute left-1/2 -translate-x-1/2 w-[90%] sm:w-[70%] bottom-[-1.2rem] md:-bottom-4 h-px bg-linear-to-r from-[#0074FF]/0 via-[#0074FF]/90 to-[#0074FF]/0 blur-[0.5px]" />
        )}
        <button
          type="button"
          onClick={handleToggle}
          className="w-full relative flex items-center justify-center py-4 mdlg:py-6 mb-4 mdlg:mb-2 group"
        >
          {headerMarkup}
          <ChevronDown
            className={`absolute right-[0.9rem] sm:right-[.5rem] w-[1.33rem] h-[1.33rem] translate-y-4 text-white transition-all  duration-200 ${isActive ? "rotate-180 opacity-40" : "rotate-0 opacity-60"}`}
            style={{
              transitionDelay: isActive ? "0s, 0.1s" : "0s, 0s",
              transitionProperty: "transform, opacity",
            }}
          />
        </button>
        {isActive && (
          <motion.div variants={sectionVariants} initial="initial" animate="animate" className="mt-2 mb-4">
            <div className="grid grid-cols-1 gap-6">
              {isActive && content}
              {isActive && phone}
            </div>
          </motion.div>
        )}
      </section>
    )
  }

  return (
    <motion.section
      className={`${sectionPadding} ${desktopBottomGap}`}
      variants={sectionVariants}
      initial="initial"
      animate="animate"
    >
      <div className="max-w-6xl mx-auto px-2 lg:px-6 lg:translate-x-[0.33rem] grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
        {content}
        {phone}
      </div>
    </motion.section>
  )
})
